/**
 * iOS subscription paywall helpers.
 *
 * Auto-renewal and "Manage in Settings → Apple ID → Subscriptions" copy is shown
 * by Apple's native App Store sheet after Buy Now — not duplicated in-app.
 *
 * Use `linksOnly` near the purchase button for required Terms / Privacy links.
 */

import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Browser } from '@capacitor/browser';
import { Capacitor } from '@capacitor/core';
import { cn } from '@/utils/cn';

const PRIVACY_POLICY_URL = import.meta.env.VITE_PRIVACY_POLICY_URL?.trim() ?? '';

async function openExternalUrl(url: string): Promise<void> {
  if (!url) return;
  if (Capacitor.isNativePlatform()) {
    await Browser.open({ url });
    return;
  }
  window.open(url, '_blank', 'noopener,noreferrer');
}

export interface IosSubscriptionLegalDisclosureProps {
  className?: string;
  compact?: boolean;
  /**
   * Terms + Privacy only (for plan cards). Renewal/manage text is in the App Store sheet.
   */
  linksOnly?: boolean;
}

function TermsPrivacyLinks({ className }: { className?: string }) {
  const navigate = useNavigate();

  return (
    <p
      className={cn(
        'flex flex-wrap items-center justify-center gap-x-1 gap-y-1 text-[10px] leading-relaxed text-[#8E8E93]',
        className,
      )}
    >
      <button
        type="button"
        onClick={() => navigate('/terms')}
        className="font-medium text-[#F33363] hover:text-[#F55C8D] underline-offset-2 hover:underline"
      >
        Terms of Service
      </button>
      {PRIVACY_POLICY_URL ? (
        <>
          <span aria-hidden="true">·</span>
          <button
            type="button"
            onClick={() => void openExternalUrl(PRIVACY_POLICY_URL)}
            className="font-medium text-[#F33363] hover:text-[#F55C8D] underline-offset-2 hover:underline"
          >
            Privacy Policy
          </button>
        </>
      ) : null}
    </p>
  );
}

export const IosSubscriptionLegalDisclosure: React.FC<IosSubscriptionLegalDisclosureProps> = ({
  className,
  compact = false,
  linksOnly = false,
}) => {
  if (linksOnly) {
    return <TermsPrivacyLinks className={className} />;
  }

  return (
    <div
      className={cn(
        'text-[11px] leading-relaxed text-[#8E8E93]',
        compact ? 'space-y-2' : 'space-y-2.5',
        className,
      )}
    >
      <p>
        Payment will be charged to your Apple ID account at confirmation of purchase.
      </p>
      <p>
        Subscription automatically renews unless auto-renew is turned off at least 24 hours
        before the end of the current period.
      </p>
      <p>Manage subscriptions in Settings → Apple ID → Subscriptions.</p>
      <TermsPrivacyLinks />
    </div>
  );
};
